import { Injectable, signal } from '@angular/core';
import { NavController } from '@ionic/angular/lazy';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { MsalAuthService } from '../auth/msal-auth.service';
import {
  SignInChallenge,
  SignUpAttributes,
  SocialIdentityProvider,
} from '../models/auth.model';
import { AlertService } from './alert.service';
import { AppStorageService } from './app-storage.service';
import { CUSTOMER } from '../constants';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly logoutSubject = new Subject<void>();
  readonly logout$ = this.logoutSubject.asObservable();
  private readonly _nonCustomerRoleRejected = signal<boolean>(false);
  /** Set when a sign-in was refused because the account isn't a customer;
   * the login page shows the explanation until the user picks another email. */
  readonly nonCustomerRoleRejected = this._nonCustomerRoleRejected.asReadonly();

  constructor(
    private msalAuthService: MsalAuthService,
    private navCtrl: NavController,
    private appStorageService: AppStorageService,
    private alertService: AlertService,
    private translateService: TranslateService,
  ) {}

  isLoggedIn(): boolean {
    return this.msalAuthService.isLoggedIn();
  }

  getAccount(): AccountInfo | null {
    return this.msalAuthService.getAccount();
  }

  /** `ppuRole` is absent for new accounts that haven't completed profile setup yet,
   * so only an explicit non-customer role (e.g. a partner) is refused. */
  hasNonCustomerRole(): boolean {
    const ppuRole = this.getAccount()?.idTokenClaims?.['ppuRole'] as
      | string
      | undefined;
    return !!ppuRole && ppuRole !== CUSTOMER;
  }

  /** Quietly ends a non-customer session (no navigation or logout toast) and flags
   * it for the login page. Returns `true` when the session was rejected. */
  async rejectNonCustomerRole(): Promise<boolean> {
    if (!this.hasNonCustomerRole()) return false;
    this.logoutSubject.next();
    this.appStorageService.setCustomerProfile(null);
    await this.msalAuthService.logout();
    this._nonCustomerRoleRejected.set(true);
    return true;
  }

  clearNonCustomerRoleRejection(): void {
    this._nonCustomerRoleRejected.set(false);
  }

  loginWithIdentityProvider(provider: SocialIdentityProvider): Promise<void> {
    return this.msalAuthService.loginWithIdentityProvider(provider);
  }

  startSignIn(email: string): Promise<SignInChallenge> {
    return this.msalAuthService.startSignIn(email);
  }

  submitOtp(otp: string): Promise<void> {
    return this.msalAuthService.submitOtp(otp);
  }

  submitPassword(password: string): Promise<void> {
    return this.msalAuthService.submitPassword(password);
  }

  startSignUp(email: string, attributes: SignUpAttributes): Promise<{ codeLength: number }> {
    return this.msalAuthService.startSignUp(email, attributes);
  }

  async logout(isExpired: boolean = false): Promise<void> {
    this.logoutSubject.next();
    this.appStorageService.setCustomerProfile(null);
    await this.msalAuthService.logout();
    this.navCtrl.navigateRoot(['/login']);
    this.alertService.successEvent(this.translateService.instant(isExpired ? 'App.Auth.Messages.SessionExpired' : 'App.Auth.Messages.LogoutSuccess'));
  }

  acquireToken(forceRefresh: boolean = false): Observable<string | null> {
    return this.msalAuthService.acquireToken(forceRefresh);
  }

  handleNativeRedirect(url: string): Observable<AuthenticationResult | null> {
    return this.msalAuthService.handleNativeRedirect(url);
  }
}
